import { useEffect, useRef, useState } from 'react'

/**
 * Animates a number from its previous value to the new value using
 * requestAnimationFrame with an ease-out curve. Returns the current
 * animated value. When the target changes, the animation restarts
 * from wherever the display currently is.
 *
 * @param target The number to animate towards
 * @param duration Animation duration in ms (default 600)
 */
export function useRollingCount(target: number, duration = 600): number {
  const [display, setDisplay] = useState(target)
  const fromRef = useRef(target)
  const startRef = useRef(0)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (display === target) return
    fromRef.current = display
    startRef.current = 0

    const from = fromRef.current
    const delta = target - from

    if (delta === 0) return

    const tick = (now: number): void => {
      if (startRef.current === 0) startRef.current = now
      const elapsed = now - startRef.current
      const t = Math.min(elapsed / duration, 1)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3)
      setDisplay(Math.round(from + delta * eased))
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        setDisplay(target)
        rafRef.current = null
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [target, duration])

  return display
}
